import { useState, useEffect, useCallback, type FormEvent } from "react";
import AnimatedSection from "@/components/base/AnimatedSection";
import { supabase } from "@/lib/supabase";

interface CasePost {
  id: string;
  title: string;
  category: string;
  content: string;
  date: string;
  image?: string;
  documents?: string[];
}

interface CaseRow {
  id: string;
  title: string;
  category: string;
  content: string;
  date: string;
  image: string | null;
  documents: string[];
}

const categories = ["전체", "강력범죄", "경제범죄", "일반범죄", "부동산", "민사 일반", "가사 및 상속"];

const BOARD_PASSWORD = "2026";
const DESKTOP_PAGE_SIZE = 6;
const MOBILE_PAGE_SIZE = 3;
const STORAGE_BUCKET = "case-images";

function mapRow(row: CaseRow): CasePost {
  return {
    id: row.id,
    title: row.title,
    category: row.category,
    content: row.content,
    date: row.date,
    image: row.image || undefined,
    documents: Array.isArray(row.documents) ? row.documents : [],
  };
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const res = await fetch(dataUrl);
  return res.blob();
}

async function uploadImage(dataUrl: string, prefix: string): Promise<string> {
  if (!dataUrl.startsWith("data:")) return dataUrl;

  const blob = await dataUrlToBlob(dataUrl);
  const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg");
  const fileName = `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const path = `case-posts/${fileName}`;

  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, blob, {
    contentType: blob.type,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

async function uploadDocuments(documents: string[]): Promise<string[]> {
  const result: string[] = [];
  for (const doc of documents) {
    result.push(await uploadImage(doc, "doc"));
  }
  return result;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("파일을 읽을 수 없습니다."));
    reader.readAsDataURL(file);
  });
}

function ImageUploadField({ label, value, onChange, onError }: {
  label: string;
  value: string;
  onChange: (dataUrl: string) => void;
  onError: (msg: string) => void;
}) {
  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      onError("이미지는 1.5MB 이하로 올려주세요.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange(dataUrl);
    } catch {
      onError("이미지를 읽지 못했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</label>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-background-300/60 bg-background-50 text-sm text-foreground-600 hover:border-primary-400 hover:text-primary-500 transition-colors">
          <i className="ri-image-add-line" />
          <span className="whitespace-nowrap">{value ? "이미지 변경" : "이미지 선택"}</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-xs text-foreground-500 hover:text-red-500 transition-colors whitespace-nowrap"
          >
            삭제
          </button>
        )}
      </div>
      {value && (
        <img
          src={value}
          alt={`${label} 미리보기`}
          className="mt-2 w-full h-32 object-cover object-top rounded-lg border border-background-200/60"
        />
      )}
    </div>
  );
}

function MultiImageUploadField({ label, values, onChange, onError }: {
  label: string;
  values: string[];
  onChange: (dataUrls: string[]) => void;
  onError: (msg: string) => void;
}) {
  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      onError("이미지는 1.5MB 이하로 올려주세요.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange([...values, dataUrl]);
    } catch {
      onError("이미지를 읽지 못했습니다. 다시 시도해주세요.");
    }
  };

  const removeAt = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</label>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-background-300/60 bg-background-50 text-sm text-foreground-600 hover:border-primary-400 hover:text-primary-500 transition-colors">
          <i className="ri-image-add-line" />
          <span className="whitespace-nowrap">이미지 추가</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
        {values.length > 0 && (
          <span className="text-xs text-foreground-500 whitespace-nowrap">{values.length}장</span>
        )}
      </div>
      {values.length > 0 && (
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
          {values.map((src, index) => (
            <div key={index} className="relative group">
              <img
                src={src}
                alt={`${label} 미리보기 ${index + 1}`}
                className="w-full h-24 object-cover object-top rounded-lg border border-background-200/60"
              />
              <button
                type="button"
                onClick={() => removeAt(index)}
                className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950/60 text-white hover:bg-red-500 transition-colors"
                aria-label="이미지 삭제"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CasesBoardSection() {
  const [cases, setCases] = useState<CasePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeCategory, setActiveCategory] = useState("전체");
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [formCategory, setFormCategory] = useState("부동산");
  const [formContent, setFormContent] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formImage, setFormImage] = useState("");
  const [formDocuments, setFormDocuments] = useState<string[]>([]);
  const [formError, setFormError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCase, setSelectedCase] = useState<CasePost | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [editTarget, setEditTarget] = useState<CasePost | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("부동산");
  const [editContent, setEditContent] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editImage, setEditImage] = useState("");
  const [editDocuments, setEditDocuments] = useState<string[]>([]);
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const PAGE_SIZE = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data, error } = await supabase
        .from("case_posts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCases((data || []).map((row) => mapRow(row as CaseRow)));
    } catch {
      setLoadError("승소사례를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [isMobile]);

  useEffect(() => {
    const hasModal = deleteTarget !== null || selectedCase !== null || editTarget !== null || lightboxImage !== null;
    if (!hasModal) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (lightboxImage !== null) {
          setLightboxImage(null);
        } else {
          setDeleteTarget(null);
          setSelectedCase(null);
          setEditTarget(null);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [deleteTarget, selectedCase, editTarget, lightboxImage]);

  const visibleCategories = categories.filter(
    (cat) => cat === "전체" || cases.some((c) => c.category === cat)
  );

  const filteredCases = activeCategory === "전체"
    ? cases
    : cases.filter((c) => c.category === activeCategory);

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / PAGE_SIZE));
  const pagedCases = filteredCases.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const lightboxDocs = selectedCase?.documents || [];
  const hasLightboxNav = lightboxDocs.length > 1;

  const goPrevImage = () => {
    if (!lightboxDocs.length) return;
    const nextIndex = (lightboxIndex - 1 + lightboxDocs.length) % lightboxDocs.length;
    setLightboxIndex(nextIndex);
    setLightboxImage(lightboxDocs[nextIndex]);
  };

  const goNextImage = () => {
    if (!lightboxDocs.length) return;
    const nextIndex = (lightboxIndex + 1) % lightboxDocs.length;
    setLightboxIndex(nextIndex);
    setLightboxImage(lightboxDocs[nextIndex]);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!formTitle.trim()) {
      setFormError("제목을 입력해주세요.");
      return;
    }
    if (!formContent.trim()) {
      setFormError("내용을 입력해주세요.");
      return;
    }
    if (formContent.length > 1000) {
      setFormError("내용은 1000자 이내로 입력해주세요.");
      return;
    }
    if (formPassword !== BOARD_PASSWORD) {
      setFormError("비밀번호가 올바르지 않습니다.");
      return;
    }

    setSubmitting(true);
    try {
      let imageUrl: string | null = null;
      if (formImage) {
        imageUrl = await uploadImage(formImage, "cover");
      }
      const documentUrls = await uploadDocuments(formDocuments);

      const { data, error } = await supabase
        .from("case_posts")
        .insert({
          title: formTitle.trim(),
          category: formCategory,
          content: formContent.trim(),
          date: new Date().toISOString().split("T")[0],
          image: imageUrl,
          documents: documentUrls,
        })
        .select();

      if (error) throw error;

      const newPost = mapRow(data[0] as CaseRow);
      setCases((prev) => [newPost, ...prev]);

      setFormTitle("");
      setFormContent("");
      setFormPassword("");
      setFormImage("");
      setFormDocuments([]);
      setShowForm(false);
      setCurrentPage(1);
    } catch {
      setFormError("저장 중 문제가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const openDelete = (id: string) => {
    setDeleteTarget(id);
    setDeletePassword("");
    setDeleteError("");
  };

  const openDetail = (id: string) => {
    const target = cases.find((c) => c.id === id);
    setSelectedCase(target ?? null);
  };

  const openEdit = (post: CasePost) => {
    setEditTarget(post);
    setEditTitle(post.title);
    setEditCategory(post.category);
    setEditContent(post.content);
    setEditImage(post.image || "");
    setEditDocuments(post.documents || []);
    setEditPassword("");
    setEditError("");
    setSelectedCase(null);
  };

  const confirmEdit = async () => {
    if (!editTarget) return;
    setEditError("");

    if (!editTitle.trim()) {
      setEditError("제목을 입력해주세요.");
      return;
    }
    if (!editContent.trim()) {
      setEditError("내용을 입력해주세요.");
      return;
    }
    if (editContent.length > 1000) {
      setEditError("내용은 1000자 이내로 입력해주세요.");
      return;
    }
    if (editPassword !== BOARD_PASSWORD) {
      setEditError("비밀번호가 올바르지 않습니다.");
      return;
    }

    setEditLoading(true);
    try {
      let imageUrl: string | null = null;
      if (editImage) {
        imageUrl = await uploadImage(editImage, "cover");
      }
      const documentUrls = await uploadDocuments(editDocuments);

      const { error } = await supabase
        .from("case_posts")
        .update({
          title: editTitle.trim(),
          category: editCategory,
          content: editContent.trim(),
          image: imageUrl,
          documents: documentUrls,
        })
        .eq("id", editTarget.id);

      if (error) throw error;

      setCases((prev) =>
        prev.map((c) =>
          c.id === editTarget.id
            ? {
                ...c,
                title: editTitle.trim(),
                category: editCategory,
                content: editContent.trim(),
                image: imageUrl || undefined,
                documents: documentUrls,
              }
            : c
        )
      );

      setEditTarget(null);
      setEditPassword("");
      setEditError("");
    } catch {
      setEditError("수정 중 문제가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setEditLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deletePassword !== BOARD_PASSWORD) {
      setDeleteError("비밀번호가 올바르지 않습니다.");
      return;
    }

    setDeleteLoading(true);
    try {
      const { error } = await supabase.from("case_posts").delete().eq("id", deleteTarget);
      if (error) throw error;

      setCases((prev) => prev.filter((c) => c.id !== deleteTarget));
      setDeleteTarget(null);
      setDeletePassword("");
      setDeleteError("");
    } catch {
      setDeleteError("삭제 중 문제가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-accent-500 text-sm font-medium tracking-widest mb-2">SUCCESSFUL CASE</p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-primary-500">
            승소사례
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
            부동산·형사전문 이승환 변호사의 주요 승소 사례를 직접 확인하세요.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="w-full flex gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible pb-1 sm:pb-0">
            {visibleCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="hidden md:flex items-center gap-2 px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
          >
            <i className="ri-add-line" />
            <span>새 글 작성</span>
          </button>
        </div>

        {showForm && (
          <AnimatedSection>
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/60 mb-8">
              <h3 className="text-lg font-bold text-foreground-950 mb-4">새 승소 사례 작성</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">제목</label>
                    <input
                      type="text"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      placeholder="사례 제목을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">카테고리</label>
                    <select
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      {categories.filter((c) => c !== "전체").map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ImageUploadField
                    label="대표 이미지"
                    value={formImage}
                    onChange={setFormImage}
                    onError={setFormError}
                  />
                  <MultiImageUploadField
                    label="판결문 사진"
                    values={formDocuments}
                    onChange={setFormDocuments}
                    onError={setFormError}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">비밀번호</label>
                  <input
                    type="password"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="게시용 비밀번호를 입력하세요"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    내용 <span className="text-foreground-400">({formContent.length}/1000자)</span>
                  </label>
                  <textarea
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    maxLength={1000}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                    placeholder="사례 내용을 입력하세요 (최대 1000자)"
                  />
                </div>
                {formError && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                  >
                    {submitting ? "저장 중..." : "등록하기"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setFormError(""); }}
                    className="px-6 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-700 font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                  >
                    취소
                  </button>
                </div>
              </form>
            </div>
          </AnimatedSection>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-background-100 rounded-xl p-4 border border-background-200/60 animate-pulse"
              >
                <div className="h-44 md:h-52 bg-background-200/60 rounded-lg mb-3" />
                <div className="h-4 bg-background-200/60 rounded w-24 mb-2" />
                <div className="h-5 bg-background-200/60 rounded w-3/4 mb-2" />
                <div className="h-4 bg-background-200/60 rounded w-full mb-1" />
                <div className="h-4 bg-background-200/60 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div className="text-center py-16 bg-background-100 rounded-xl border border-background-200/60">
            <p className="text-foreground-600 text-sm mb-4">{loadError}</p>
            <button
              type="button"
              onClick={fetchCases}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
            >
              다시 시도
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCases.length === 0 ? (
              <div className="text-center py-16 bg-background-100 rounded-xl border border-background-200/60 col-span-full">
                <p className="text-foreground-500 text-sm">해당 카테고리의 사례가 없습니다.</p>
              </div>
            ) : (
              pagedCases.map((item, idx) => (
                <AnimatedSection key={item.id} delay={idx * 80}>
                  <div
                    onClick={() => openDetail(item.id)}
                    className="bg-background-100 rounded-xl p-4 border border-background-200/60 hover:border-primary-300/60 transition-all cursor-pointer group flex flex-col h-full"
                  >
                    {item.image ? (
                      <div className="hidden md:block mb-3 overflow-hidden rounded-lg">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-full h-44 md:h-52 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="hidden md:flex mb-3 h-36 md:h-44 bg-background-200/50 rounded-lg items-center justify-center">
                        <i className="ri-image-line text-2xl text-foreground-300" />
                      </div>
                    )}
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-600 border border-primary-100">
                          {item.category}
                        </span>
                        <span className="text-xs text-foreground-400">{item.date}</span>
                      </div>
                      <h3 className="text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3 mb-3 flex-1">
                        {item.content}
                      </p>
                      <div className="flex items-center gap-1 text-xs font-medium text-primary-500">
                        <span>자세히 보기</span>
                        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))
            )}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/60 bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="이전 페이지"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  currentPage === page
                    ? "bg-primary-500 text-white"
                    : "border border-background-200/60 bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/60 bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="다음 페이지"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="relative z-10 w-full max-w-sm bg-background-50 rounded-xl border border-background-200/60 p-6 animate-modal-in">
            <h3 className="font-display text-lg font-bold text-foreground-950 mb-1">게시글 삭제</h3>
            <p className="text-sm text-foreground-500 mb-4">삭제하려면 비밀번호를 입력해주세요.</p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              autoFocus
              className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
              placeholder="비밀번호 입력"
            />
            {deleteError && (
              <p className="text-sm text-red-500 mt-2">{deleteError}</p>
            )}
            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="flex-1 px-5 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
              >
                {deleteLoading ? "삭제 중..." : "삭제"}
              </button>
              <button
                type="button"
                onClick={() => { setDeleteTarget(null); setDeleteError(""); }}
                className="flex-1 px-5 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-700 font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setSelectedCase(null)}
          />
          <div className="relative z-10 w-full max-w-2xl max-h-[85dvh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8 animate-modal-in">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-600 border border-primary-100">
                    {selectedCase.category}
                  </span>
                  <span className="text-xs text-foreground-400">{selectedCase.date}</span>
                </div>
                <h3 className="font-display text-lg md:text-xl font-bold text-foreground-950">
                  {selectedCase.title}
                </h3>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => openEdit(selectedCase)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/60 bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 transition-colors"
                  aria-label="수정"
                >
                  <i className="ri-edit-line" />
                </button>
                <button
                  onClick={() => { openDelete(selectedCase.id); setSelectedCase(null); }}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/60 bg-background-100 text-foreground-500 hover:text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors"
                  aria-label="삭제"
                >
                  <i className="ri-delete-bin-line" />
                </button>
                <button
                  onClick={() => setSelectedCase(null)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/60 bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors"
                  aria-label="닫기"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
            </div>
            {selectedCase.image && (
              <img
                src={selectedCase.image}
                alt={selectedCase.title}
                className="w-full h-48 md:h-64 object-cover object-top rounded-lg mb-4"
              />
            )}
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-wrap">
              {selectedCase.content}
            </p>
            {selectedCase.documents && selectedCase.documents.length > 0 && (
              <div className="mt-5 pt-5 border-t border-background-200/60">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-file-text-line text-accent-500" />
                  <span className="text-sm font-semibold text-foreground-900">판결문</span>
                  <span className="text-xs text-foreground-400">({selectedCase.documents.length}장 · 클릭 시 확대)</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {selectedCase.documents.map((doc, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => { setLightboxIndex(index); setLightboxImage(doc); }}
                      className="relative w-28 h-36 bg-background-100 rounded-lg border border-background-200/60 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer group shrink-0"
                      aria-label={`판결문 ${index + 1} 확대 보기`}
                    >
                      <img
                        src={doc}
                        alt={`${selectedCase.title} 판결문 ${index + 1}`}
                        className="w-full h-full object-cover object-top"
                      />
                      <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-colors">
                        <i className="ri-zoom-in-line text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {editTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setEditTarget(null)}
          />
          <div className="relative z-10 w-full max-w-2xl max-h-[85dvh] overflow-y-auto bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8 animate-modal-in">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-display text-lg md:text-xl font-bold text-foreground-950">게시글 수정</h3>
              <button
                onClick={() => setEditTarget(null)}
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/60 bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors shrink-0"
                aria-label="닫기"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); confirmEdit(); }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">제목</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="사례 제목을 입력하세요"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">카테고리</label>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    {categories.filter((c) => c !== "전체").map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  내용 <span className="text-foreground-400">({editContent.length}/1000자)</span>
                </label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  maxLength={1000}
                  rows={6}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  placeholder="사례 내용을 입력하세요 (최대 1000자)"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ImageUploadField
                  label="대표 이미지"
                  value={editImage}
                  onChange={setEditImage}
                  onError={setEditError}
                />
                <MultiImageUploadField
                  label="판결문 사진"
                  values={editDocuments}
                  onChange={setEditDocuments}
                  onError={setEditError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">비밀번호</label>
                <input
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="수정용 비밀번호를 입력하세요"
                />
              </div>
              {editError && (
                <p className="text-sm text-red-500">{editError}</p>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={editLoading}
                  className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
                >
                  {editLoading ? "수정 중..." : "수정하기"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditTarget(null)}
                  className="px-6 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-700 font-medium rounded-lg transition-colors whitespace-nowrap text-sm"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {lightboxImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8">
          <div
            className="absolute inset-0 bg-foreground-950/80"
            onClick={() => setLightboxImage(null)}
          />
          <div className="relative z-10 w-full max-w-4xl flex items-center justify-center">
            <img
              src={lightboxImage}
              alt="판결문 확대 보기"
              className="max-h-[90dvh] max-w-full w-auto object-contain rounded-lg"
            />
            {hasLightboxNav && (
              <>
                <button
                  type="button"
                  onClick={goPrevImage}
                  className="absolute left-2 md:left-4 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 hover:bg-background-200 transition-colors"
                  aria-label="이전 이미지"
                >
                  <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
                </button>
                <button
                  type="button"
                  onClick={goNextImage}
                  className="absolute right-2 md:right-4 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 hover:bg-background-200 transition-colors"
                  aria-label="다음 이미지"
                >
                  <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
                </button>
              </>
            )}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-foreground-950/70 text-white text-xs whitespace-nowrap">
              {lightboxIndex + 1} / {lightboxDocs.length}
            </div>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              className="absolute -top-2 -right-2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-foreground-700 hover:bg-background-200 transition-colors"
              aria-label="닫기"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}